import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogTitle, MatDialogContent, MatDialogActions, MatDialogClose } from '@angular/material/dialog';
import { Location } from '../../../../../models/locations-models/location';
import { FieldGroup, getChangeableGmbFields } from '../../../location-utils';
import {
  BulkActionPopupComponent,
  BulkActionPopupData,
  SelectedFieldsRadioButton,
} from '../bulk-action-popup/bulk-action-popup.component';
import { FormBuilder, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { MatButton } from '@angular/material/button';
import { MatMenuItem } from '@angular/material/menu';
import { NgClass } from '@angular/common';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { MatIcon } from '@angular/material/icon';

interface BulkAcceptDifferencesDialogData extends BulkActionPopupData {
  locations: Location[];
}

@Component({
    selector: 'app-bulk-accept-differences-popup',
    templateUrl: './bulk-accept-differences-popup.component.html',
    styleUrls: ['./bulk-accept-differences-popup.component.scss',
                '../bulk-action-popup/bulk-action-popup.component.scss'],
    standalone: true,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatDialogTitle,
        MatIcon,
        MatDialogContent,
        MatRadioGroup,
        MatRadioButton,
        MatCheckbox,
        NgClass,
        MatMenuItem,
        MatDialogActions,
        MatButton,
        MatDialogClose,
        TranslateModule,
    ],
})
export class BulkAcceptDifferencesPopupComponent extends BulkActionPopupComponent implements OnInit {
  differenceCountMap: Map<string, number> = new Map<string, number>();
  isBulkAcceptConfirmWindow: boolean;

  protected override readonly selectedFieldsRadios: SelectedFieldsRadioButton[] = [
    SelectedFieldsRadioButton.ALL,
    SelectedFieldsRadioButton.NONE,
  ];

  constructor(
      public dialogRef: MatDialogRef<BulkAcceptDifferencesPopupComponent>,
      @Inject(MAT_DIALOG_DATA) public data: BulkAcceptDifferencesDialogData,
      protected fb: FormBuilder,
  ) {
    super(data, dialogRef, fb);
  }

  override ngOnInit() {
    this.getSelectableFields = getChangeableGmbFields;
    this.countFieldDifferences();
    super.ngOnInit();
  }

  protected override initSelectedFieldsFormControls(): FormControl<boolean>[] {
    return this.getSelectableFields().map((fieldID) =>
        new FormControl({ value: false, disabled: this.getDifferencesCountByFieldId(fieldID) === 0 }),
    );
  }

  protected override generateResult() {
    let fieldsToAcceptDifference = this.getSelectedFieldIDs();

    if (fieldsToAcceptDifference.length === 0) {
      return null;
    }

    return {
      fieldsToAcceptDifference: fieldsToAcceptDifference,
    };
  }

  /**
   * Counts the differences per a certain field and updates differenceCountMap
   */
  countFieldDifferences() {
    if (this.data.locations.length > 0) {
      this.data.locations.forEach((loc) => {
        for (const key in loc.differences) {
          if (loc.differences[key]['ignored'] === false) {
            // check whether the element exists
            if (!this.differenceCountMap.has(key)) {
              // check if we can create it
              if (!this.getSelectableFields().includes(key)) {
                // skip the currently iterated key as it's not a selectable field
                continue;
              }

              this.differenceCountMap.set(key, 0);
            }

            let count = this.differenceCountMap.get(key);
            this.differenceCountMap.set(key, count + 1);
          }
        }
      });
    }
  }

  isFieldFCDisabled(fieldID: string): boolean {
    return this.getFieldFCByFieldID(fieldID)?.disabled;
  }

  isGroupFieldCheckboxDisabled(fieldIDs: string[]) {
    return fieldIDs.every(fieldID => this.isFieldFCDisabled(fieldID));
  }

  /**
   * Returns a list of fieldIds of a given FieldGroup that will be selected for bulk accept confirmation
   */
  getSelectedFieldIDsByGroup(fieldGroup: FieldGroup): string[] {
    return this.getFieldIDsByGroup(fieldGroup).filter(fieldID => this.getSelectedFieldIDs().includes(fieldID));
  }

  /**
   * Get an amount of differences by the fieldId
   */
  getDifferencesCountByFieldId(fieldId: string): number {
    return this.differenceCountMap.get(fieldId) ? this.differenceCountMap.get(fieldId) : 0;
  }

  /**
   *  Changes the dialog window
   */
  nextDialogWindow() {
    this.isBulkAcceptConfirmWindow = !this.isBulkAcceptConfirmWindow;
  }
}
